<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :loading="tableIsLoading"
            :actions="actions"
            ajax-path="pbxware/extensions"
            :ajax-params="{tenant_id: $route.meta.serverId}"
            @ajaxResponse="response => plans = response.plans"
        />
        <app-modal
            :show.sync="showEditModal"
            width="lg:w-4/5"
            bg="bg-gray-200"
            @close="editModalClosed"
        >
            <template slot="header">
                <div class="flex items-center justify-between">
                    <div>Edit Extension</div>
                    <checkbox 
                        v-model="editForm.show_advanced_options"
                        class="text-xs"
                    >Show advanced options</checkbox>
                </div>
            </template>
            <template slot="body">                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div class="card mb-5">
                        <h2 class="card-header">General</h2>
                        <div class="card-body">
                            <app-input
                                v-model="editForm.ext"
                                placeholder="Extension"
                                :error="editForm.errors.get('ext')"
                                @updated="editForm.errors.clear('ext')"
                                :loading="generalInputIsLoading"
                                :required="true"
                            />
                            <app-input
                                v-model="editForm.user_title"
                                placeholder="Title"
                                :error="editForm.errors.get('title')"
                                @updated="editForm.errors.clear('title')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-input
                                v-model="editForm.name"
                                placeholder="Name"
                                :error="editForm.errors.get('name')"
                                @updated="editForm.errors.clear('name')"
                                :loading="generalInputIsLoading"
                                :required="true"
                            />
                            <app-input
                                type="email"
                                v-model="editForm.email"
                                placeholder="Email"
                                :error="editForm.errors.get('email')"
                                @updated="editForm.errors.clear('email')"
                                :loading="generalInputIsLoading"
                                :required="true"
                            />
                            <app-select 
                                v-model="editForm.ua_id"
                                placeholder="UAD"
                                :error="editForm.errors.get('ua_id')"
                                @updated="editForm.errors.clear('ua_id')"
                                :options="uad"
                                value-key="id"
                                label-key="name"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-select 
                                v-model="editForm.location"
                                placeholder="UAD Location"
                                :error="editForm.errors.get('location')"
                                @updated="editForm.errors.clear('location')"
                                :options="[
                                    {id: 1, name: 'Local'},
                                    {id: 2, name: 'Remote'},
                                ]"
                                value-key="id"
                                label-key="name"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-input
                                v-model="editForm.user_location"
                                placeholder="Location"
                                :error="editForm.errors.get('user_location')"
                                @updated="editForm.errors.clear('user_location')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-input
                                v-model="editForm.user_label"
                                placeholder="Label"
                                :error="editForm.errors.get('user_label')"
                                @updated="editForm.errors.clear('user_label')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-select 
                                v-model="editForm.department"
                                placeholder="Department"
                                :error="editForm.errors.get('department')"
                                @updated="editForm.errors.clear('department')"
                                :options="[]"
                                :loading="generalInputIsLoading"
                                :required="true"
                            />
                            <app-select 
                                v-model="editForm.type"
                                placeholder="User Type"
                                :error="editForm.errors.get('type')"
                                @updated="editForm.errors.clear('type')"
                                :options="[
                                    {id: 'user', name: 'User'},
                                    {id: 'friend', name: 'Friend'},
                                    {id: 'peer', name: 'Peer'},
                                ]"
                                value-key="id"
                                label-key="name"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-input
                                v-model="editForm.dtmfmode"
                                placeholder="DTMF Mode"
                                :error="editForm.errors.get('dtmfmode')"
                                @updated="editForm.errors.clear('dtmfmode')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                            <app-input
                                v-model="editForm.context"
                                placeholder="Context"
                                :error="editForm.errors.get('context')"
                                @updated="editForm.errors.clear('context')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                                :disable="true"
                            />
                            <app-select 
                                v-model="editForm.status"
                                placeholder="Status"
                                :error="editForm.errors.get('status')"
                                @updated="editForm.errors.clear('status')"
                                :options="[
                                    {id: '1', name: 'Active'},
                                    {id: '0', name: 'Not Active'},
                                    {id: '2', name: 'Suspended'},
                                ]"
                                value-key="id"
                                label-key="name"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                            />
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-body">
                            <checkbox 
                                v-model="editForm.billing"
                                class="tracking-widest uppercase font-thin"
                                check-value="1"
                                uncheck-value="0"
                            >Billing</checkbox>
                            <div 
                                class="mt-5" 
                                v-if="editForm.billing === '1'"
                            >
                                <app-select 
                                    v-model="editForm.service_plan"
                                    placeholder="Service Plan"
                                    :error="editForm.errors.get('service_plan')"
                                    @updated="editForm.errors.clear('service_plan')"
                                    :options="plans"
                                    label-key="name"
                                    value-key="id"
                                    :loading="generalInputIsLoading"
                                />
                                <app-input
                                    v-model="editForm.slave_accountcode"
                                    placeholder="Master Account Code"
                                    :error="editForm.errors.get('slave_accountcode')"
                                    @updated="editForm.errors.clear('slave_accountcode')"
                                    :loading="generalInputIsLoading"
                                />
                                <app-input
                                    v-model="editForm.reminder_balance"
                                    placeholder="Reminder Balance"
                                    :error="editForm.errors.get('reminder_balance')"
                                    @updated="editForm.errors.clear('reminder_balance')"
                                    :loading="generalInputIsLoading"
                                />
                                <app-input
                                    v-model="editForm.creditlimit"
                                    placeholder="Credit Limit"
                                    :error="editForm.errors.get('creditlimit')"
                                    @updated="editForm.errors.clear('creditlimit')"
                                    :loading="generalInputIsLoading"
                                />
                                <checkbox 
                                    v-model="editForm.slave"
                                    class="mt-4"
                                >Slave</checkbox>
                            </div>
                        </div>                            
                    </div>
                    <div class="card">
                        <h2 class="card-header">Authentication</h2>
                        <div class="card-body">
                            <app-input
                                v-model="editForm.username"
                                placeholder="Username"
                                :error="editForm.errors.get('username')"
                                @updated="editForm.errors.clear('username')"
                                :loading="generalInputIsLoading"
                                :required="true"
                                :disable="true"
                            />
                            <app-input
                                v-model="editForm.authname"
                                placeholder="Authname"
                                :error="editForm.errors.get('authname')"
                                @updated="editForm.errors.clear('authname')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                                :limit="20"
                            />
                            <app-input
                                v-model="editForm.__auth"
                                placeholder="Auth"
                                :error="editForm.errors.get('__auth')"
                                @updated="editForm.errors.clear('__auth')"
                                :loading="generalInputIsLoading"
                                v-if="editForm.show_advanced_options"
                                :limit="20"
                            />
                            <app-input
                                v-model="editForm.secret"
                                placeholder="Secret"
                                :error="editForm.errors.get('secret')"
                                @updated="editForm.errors.clear('secret')"
                                :loading="generalInputIsLoading"
                                :required="true"
                            />
                            <app-input
                                v-model="editForm.extpassword"
                                placeholder="User Password"
                                :error="editForm.errors.get('extpassword')"
                                @updated="editForm.errors.clear('extpassword')"
                                :loading="generalInputIsLoading"
                                :required="true"
                                v-if="false"
                            />
                            <app-input
                                v-model="editForm.pin"
                                placeholder="PIN"
                                :error="editForm.errors.get('pin')"
                                @updated="editForm.errors.clear('pin')"
                                :loading="generalInputIsLoading"
                                :required="true"
                                :limit="4"
                                :integer="true"
                            />
                        </div>
                    </div>                    
                </div>
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white mr-3" 
                        @click="showEditModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue"
                        @click="update"
                        :loading="editFormIsLoading"
                        :disable="generalInputIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Extension</template>
            <template slot="body">
                <div>
                    <app-input
                        v-model="addForm.name"
                        placeholder="Name"
                        :error="addForm.errors.get('name')"
                        @updated="addForm.errors.clear('name')"
                        focus
                        required
                    />
                    <app-input
                        type="email"
                        v-model="addForm.email"
                        placeholder="Email"
                        :error="addForm.errors.get('email')"
                        @updated="addForm.errors.clear('email')"
                        required
                    />
                    <app-select
                        v-model="addForm.prot"
                        placeholder="Protocol"
                        :error="addForm.errors.get('prot')"
                        @updated="addForm.errors.clear('prot')"
                        :options="[
                            {id: 'sip', label: 'SIP'}, 
                            {id: 'iax', label: 'IAX'}
                        ]"
                        value-key="id"
                        required
                    />
                    <app-select
                        v-model="addForm.ua"
                        placeholder="UAD"
                        :error="addForm.errors.get('ua')"
                        @updated="addForm.errors.clear('ua')"
                        :options="uad"
                        value-key="id"
                        label-key="name"
                        required
                    />
                    <app-select
                        v-model="addForm.location"
                        placeholder="Location"
                        :error="addForm.errors.get('location')"
                        @updated="addForm.errors.clear('location')"
                        :options="[
                            {id: 1, label: 'Local'},
                            {id: 2, label: 'Remote'}
                        ]"
                        value-key="id"
                        required
                    />
                    <div class="mt-5 flex items-center justify-end">
                        <app-button 
                            class="btn-white" 
                            @click="showAddModal = false"
                            :disable="addFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="store"
                            :loading="addFormIsLoading"
                        >Add</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    export default {
        data() {
            return {
                plans: [],
                addForm: new Form({
                    name: null,
                    email: null,
                    prot: null,
                    ua: null,
                    location: null,
                    status: 1,
                }),
                editForm: new Form({
                    show_advanced_options: false,
                    id: null,
                    ext: null,
                    name: null,
                    email: null,
                    department: [],
                    username: null,
                    pin: null,
                    secret: null,
                    extpassword: null,
                    username: null,
                    secret: null,
                    user_title: null,
                    user_location: null,
                    user_label: null,
                    dtmfmode: null,
                    context: null,
                    authname: null,
                    __auth: null,
                    type: null,
                    ua_id: null,
                    location: null,
                    status: null,
                    billing: '0',
                    service_plan: null,
                    slave: false,
                    slave_accountcode: null,
                    reminder_balance: null,
                    creditlimit: null,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                q: null,
                generalInputIsLoading: false,
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Extension',
                        field: 'ext',
                        sorter: 'string',
                        width: 100,
                    },
                    {
                        title: 'Service Plan',
                        field: 'billing.service_plan_for_human',
                        headerSort: false,
                        minWidth: 150,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Account Balance',
                        field: 'billing.account_balance_for_human',
                        sorter: 'number',
                        width: 150,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Credit Limit',
                        field: 'billing.credit_limit',
                        sorter: 'number',
                        width: 130,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'User Agent',
                        field: 'ua_fullname',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        width: 100,
                        formatter({_cell}) {
                            return _cell.row.data.status == 'enabled' ? 'Active' : _cell.row.data.status
                        }
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: extensions => {
                            this.edit(extensions[0].extension_id);
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: extensions => {
                            this.deleteItem(extensions[0]);
                        }
                    },
                    {
                        label: 'Refresh',
                        icon: () => import('@/icons/Refresh.vue'),
                        handler: this.getExtensions,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                item: 'pbxware/extension/item',
                uad: 'pbxware/extension/uad',
            }),
        },
        methods: {            
            edit(id) {
                this.showEditModal = true;
                this.generalInputIsLoading = true;
                this.$store.dispatch('pbxware/extension/edit', id)
                    .then(({ data }) => {
                        this.generalInputIsLoading = false;
                        this.editForm.set({id});
                        this.editForm.set(data['general']);
                        this.editForm.set({slave_accountcode: data.billing[id].master_code})
                    })
                    .catch(() => {
                        this.generalInputIsLoading = false;
                    })
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('pbxware/extension/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.getExtensions();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Configuration saved successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);

                        if (this.editForm.errors.has('error')) {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: this.editForm.errors.get('error')
                            });
                        } else {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: response.data.message
                            });
                        }
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('pbxware/extension/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.getExtensions();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Extension was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deleteItem(item) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${item.name}</strong>" extension.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('pbxware/extension/delete', item.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.getExtensions();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Extension was successfully deleted.'
                        })
                    }
                })
                
            },
            search() {

            }
        }
    }
</script>

<style lang="scss" scoped>
    .edit-form {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        grid-gap: theme('spacing.5');
    }
</style>